<ng-template #noticeLine>
  <p
    *ngIf="notice"
    class="m-0 text-sm"
    [ngClass]="notice.tone === 'error' ? 'text-status-red' : 'text-gray-700'"
    [attr.role]="notice.tone === 'error' ? 'alert' : 'status'"
  >
    {{ notice.key | translate: notice.params }}
  </p>
</ng-template>

<ng-template #backButton>
  <button mat-button color="primary" type="button" (click)="back()">
    {{ 'Login.BackToSignIn' | translate }}
  </button>
</ng-template>

<ng-template #codeForm_>
  <form [formGroup]="codeForm" (ngSubmit)="verify()" class="flex flex-col gap-1">
    <mat-form-field appearance="outline">
      <mat-label>
        {{ (step === 'scan' ? 'Login.MfaCodeFromApp' : 'Login.Code') | translate }}
      </mat-label>
      <input
        matInput
        id="second-factor-code"
        formControlName="code"
        inputmode="numeric"
        autocomplete="one-time-code"
        maxlength="7"
      />
    </mat-form-field>
    <ng-container *ngTemplateOutlet="noticeLine"></ng-container>
    <button
      mat-flat-button
      color="primary"
      type="submit"
      class="mt-2 h-12 w-full"
      [disabled]="busy || !codeIsComplete"
      [attr.aria-busy]="busy"
    >
      {{ (busy ? 'Login.Verifying' : 'Login.Verify') | translate }}
    </button>
  </form>
</ng-template>

<ng-container [ngSwitch]="step">
  <!-- Setting up: which kind of factor. -->
  <ng-container *ngSwitchCase="'choose'">
    <h1 class="m-0 text-2xl font-medium">{{ 'Login.MfaChooseTitle' | translate }}</h1>
    <p class="m-0 text-gray-700">
      {{
        (challenge.trustDays ? 'Login.MfaChooseOrganization' : 'Login.MfaChooseAccount') | translate
      }}
    </p>
    <div class="flex flex-col gap-2">
      <button
        type="button"
        class="flex min-h-16 w-full cursor-pointer items-center gap-3 rounded border border-solid border-pill bg-surface p-2.5 text-left hover:bg-surface-muted"
        [disabled]="busy"
        (click)="choosePhone()"
      >
        <mat-icon aria-hidden="true" class="flex-none text-theme-600">smartphone</mat-icon>
        <span>
          <span class="block font-semibold">{{ 'Login.MfaPhoneOption' | translate }}</span>
          <span class="block text-sm text-gray-600">{{ 'Login.MfaPhoneOptionHint' | translate }}</span>
        </span>
      </button>
      <button
        type="button"
        class="flex min-h-16 w-full cursor-pointer items-center gap-3 rounded border border-solid border-pill bg-surface p-2.5 text-left hover:bg-surface-muted"
        [disabled]="busy"
        (click)="chooseApp()"
      >
        <mat-icon aria-hidden="true" class="flex-none text-theme-600">phonelink_lock</mat-icon>
        <span>
          <span class="block font-semibold">{{ 'Login.MfaAppOption' | translate }}</span>
          <span class="block text-sm text-gray-600">{{ 'Login.MfaAppOptionHint' | translate }}</span>
        </span>
      </button>
    </div>
    <ng-container *ngTemplateOutlet="noticeLine"></ng-container>
    <div class="flex justify-center">
      <ng-container *ngTemplateOutlet="backButton"></ng-container>
    </div>
  </ng-container>

  <!-- Setting up a phone: the number. -->
  <ng-container *ngSwitchCase="'number'">
    <h1 class="m-0 text-2xl font-medium">{{ 'Login.MfaNumberTitle' | translate }}</h1>
    <p class="m-0 text-gray-700">{{ 'Login.MfaNumberIntro' | translate }}</p>
    <form [formGroup]="numberForm" (ngSubmit)="submitNumber()" class="flex flex-col gap-1">
      <mat-form-field appearance="outline" hideRequiredMarker>
        <mat-label>{{ 'Login.MfaMobileNumber' | translate }}</mat-label>
        <input
          matInput
          id="second-factor-number"
          formControlName="phoneNumber"
          type="tel"
          autocomplete="tel"
        />
      </mat-form-field>
      <ng-container *ngTemplateOutlet="noticeLine"></ng-container>
      <button
        mat-flat-button
        color="primary"
        type="submit"
        class="mt-2 h-12 w-full"
        [disabled]="busy || numberForm.invalid || sendLimitReached"
        [attr.aria-busy]="busy"
      >
        {{ (busy ? 'Login.SendingCode' : 'Login.MfaTextMe') | translate }}
      </button>
    </form>
    <div *ngIf="!adding" class="flex justify-center">
      <button mat-button color="primary" type="button" (click)="chooseDifferent()">
        {{ 'Login.MfaChooseDifferent' | translate }}
      </button>
    </div>
  </ng-container>

  <!-- Setting up an authenticator app: shown once. -->
  <ng-container *ngSwitchCase="'scan'">
    <h1 class="m-0 text-2xl font-medium">{{ 'Login.MfaQrTitle' | translate }}</h1>
    <p class="m-0 text-gray-700">{{ 'Login.MfaQrIntro' | translate }}</p>
    <div class="flex justify-center">
      <img *ngIf="qrDataUrl" class="h-48 w-48" [src]="qrDataUrl" [alt]="'Login.MfaQrAlt' | translate" />
    </div>
    <div class="flex flex-col gap-0.5 text-sm text-gray-600">
      <span>{{ 'Login.MfaKeyPrompt' | translate }}</span>
      <code class="break-all font-mono text-sm text-theme-black">{{ manualKey }}</code>
    </div>
    <ng-container *ngTemplateOutlet="codeForm_"></ng-container>
    <div *ngIf="!adding" class="flex justify-center">
      <button mat-button color="primary" type="button" (click)="chooseDifferent()">
        {{ 'Login.MfaChooseDifferent' | translate }}
      </button>
    </div>
  </ng-container>

  <!-- A code, at sign-in or confirming a new phone. -->
  <ng-container *ngSwitchCase="'code'">
    <h1 class="m-0 text-2xl font-medium">
      {{ (challenge.enrolment ? 'Login.MfaEnterCodeTitle' : 'Login.MfaTitle') | translate }}
    </h1>

    <ng-container *ngIf="cannotSend; else canAnswer">
      <p class="m-0 text-gray-700">{{ 'Login.MfaCannotSend' | translate: { hint: phoneHint } }}</p>
      <button
        *ngIf="!challenge.emailFallback"
        mat-flat-button
        color="primary"
        type="button"
        class="h-12 w-full"
        [disabled]="busy"
        (click)="retry()"
      >
        {{ 'Login.MfaTryAgain' | translate }}
      </button>
      <button
        *ngIf="challenge.emailFallback"
        mat-flat-button
        color="primary"
        type="button"
        class="h-12 w-full"
        [disabled]="busy"
        (click)="useEmail()"
      >
        {{ 'Login.MfaEmailInstead' | translate }}
      </button>
      <ng-container *ngTemplateOutlet="noticeLine"></ng-container>
    </ng-container>

    <ng-template #canAnswer>
      <p class="m-0 text-gray-700">
        <ng-container *ngIf="challenge.emailFallback">
          {{ 'Login.MfaNewBrowser' | translate }}
        </ng-container>
        <ng-container *ngIf="factor === 'totp'">{{ 'Login.MfaCodeIntroApp' | translate }}</ng-container>
        <ng-container *ngIf="factor === 'sms' && channel === 'call'">
          {{ 'Login.MfaCallSent' | translate: { hint: phoneHint } }}
        </ng-container>
        <ng-container *ngIf="factor === 'sms' && channel === 'sms'">
          {{
            (challenge.enrolment ? 'Login.MfaConfirmSent' : 'Login.MfaCodeSent')
              | translate: { hint: phoneHint }
          }}
        </ng-container>
      </p>
      <!-- No "remember this browser" choice: a completed check always trusts it, except for a super user. -->
      <p *ngIf="challenge.trustDays" class="m-0 text-gray-700">
        {{ 'Login.NewDeviceTrust' | translate: { days: challenge.trustDays } }}
      </p>
      <ng-container *ngTemplateOutlet="codeForm_"></ng-container>
    </ng-template>

    <div class="flex flex-wrap justify-center gap-2">
      <ng-container *ngIf="factor === 'sms' && !cannotSend">
        <button
          mat-button
          color="primary"
          type="button"
          [disabled]="busy || resendPaused || sendLimitReached"
          (click)="sendCode(channel)"
        >
          {{ (channel === 'call' ? 'Login.MfaCallAgain' : 'Login.ResendCode') | translate }}
        </button>
        <button
          *ngIf="channel === 'sms'"
          mat-button
          color="primary"
          type="button"
          [disabled]="busy || resendPaused || sendLimitReached"
          (click)="sendCode('call')"
        >
          {{ 'Login.MfaCallInstead' | translate }}
        </button>
        <button
          *ngIf="channel === 'call'"
          mat-button
          color="primary"
          type="button"
          [disabled]="busy || resendPaused || sendLimitReached"
          (click)="sendCode('sms')"
        >
          {{ 'Login.MfaTextInstead' | translate }}
        </button>
      </ng-container>
      <button
        *ngIf="otherFactor as other"
        mat-button
        color="primary"
        type="button"
        [disabled]="busy"
        (click)="switchTo(other)"
      >
        {{ (other === 'totp' ? 'Login.MfaUseApp' : 'Login.MfaTextInstead') | translate }}
      </button>
      <button
        *ngIf="challenge.emailFallback && !cannotSend"
        mat-button
        color="primary"
        type="button"
        [disabled]="busy"
        (click)="useEmail()"
      >
        {{ 'Login.MfaEmailInstead' | translate }}
      </button>
      <button
        *ngIf="cannotSend && challenge.emailFallback"
        mat-button
        color="primary"
        type="button"
        [disabled]="busy"
        (click)="retry()"
      >
        {{ 'Login.MfaTryAgain' | translate }}
      </button>
      <button
        *ngIf="challenge.enrolment; else backButton"
        mat-button
        color="primary"
        type="button"
        (click)="useDifferentNumber()"
      >
        {{ 'Login.MfaUseDifferentNumber' | translate }}
      </button>
    </div>
  </ng-container>
</ng-container>
